'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
/**
 * React Query Devtools component for development
 *
 * Provides debugging tools for @tanstack/react-query.
 * Automatically shows in development mode only.
 * Can be explicitly enabled/disabled via the `enabled` prop.
 *
 * @example
 * ```tsx
 * // app/layout.tsx or QueryClientProvider wrapper
 * import { ReactQueryDevtools } from '@sandstone/dev-tools/react-query-devtools';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <QueryClientProvider client={queryClient}>
 *       {children}
 *       <ReactQueryDevtools />
 *     </QueryClientProvider>
 *   );
 * }
 * ```
 */
export function ReactQueryDevtoolsComponent({ enabled, initialIsOpen = false, ...props }) {
    // Check sessionStorage for toggle state
    const [shouldShow, setShouldShow] = useState(() => {
        if (enabled !== undefined) {
            return enabled;
        }
        if (process.env.NODE_ENV !== 'development') {
            return false;
        }
        // Check sessionStorage (controlled by dev-tools toggle panel)
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem('dev-tools:react-query-devtools-enabled');
            return stored === 'true';
        }
        return false;
    });
    // Listen for storage changes
    useEffect(() => {
        const handleStorageChange = () => {
            const stored = sessionStorage.getItem('dev-tools:react-query-devtools-enabled');
            setShouldShow(stored === 'true');
        };
        window.addEventListener('storage', handleStorageChange);
        // Poll sessionStorage since storage event doesn't fire in same tab
        const interval = setInterval(handleStorageChange, 500);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);
    if (!shouldShow) {
        return null;
    }
    return _jsx(ReactQueryDevtools, { initialIsOpen: initialIsOpen, ...props });
}
